/**
 * Dropdown Enhancer - Reusable searchable dropdown component
 *
 * Converts native select elements into searchable, accessible comboboxes.
 * Can be used across the application for consistent dropdown UX.
 *
 * Features:
 * - Progressive enhancement (fallback to native select)
 * - Keyboard navigation (Arrow keys, Enter, Escape)
 * - Search/filter functionality
 * - ARIA attributes for accessibility
 * - Customizable via prefix parameter
 * - Parent-child filtering support
 *
 * Usage:
 * ```typescript
 * const enhancer = new DropdownEnhancer({
 *   prefix: 'my-component',  // CSS class prefix
 *   searchable: true,         // Enable search functionality
 *   buildClearOption: true,   // Add "Clear selection" option
 * });
 *
 * const select = document.querySelector('select');
 * enhancer.enhance(select, 1); // level/id for this dropdown
 * ```
 */

/* ============================================================================
 * Type Definitions
 * ========================================================================== */

/**
 * Dropdown configuration options
 */
export interface DropdownConfig {
  prefix?: string;  // CSS class prefix (e.g., 'config', 'page')
  searchable?: boolean;  // Enable search functionality
  buildClearOption?: boolean;  // Add "Clear selection" option
  clearLabels?: {
    [level: number]: string;  // Custom clear labels per level
  };
  autoOpenOnFocus?: boolean;  // Auto-open dropdown on focus
  filterParentId?: boolean;  // Enable parent-child filtering
  hidePrices?: boolean;  // Hide ALL prices from option text (e.g., "(+ 5,00 €)")
  hideZeroPrices?: boolean;  // Hide ONLY zero prices from option text (e.g., "- 0,00 € *")
  /**
   * OPT-IN overlay-grow open-reveal (model-finder dropdown surfaces only — ADR-0010
   * 2026-06-09, supersedes D39). When set, opening a list keeps it a TRUE OVERLAY
   * (`position: absolute; top: 100%` — it floats over the fields/CTA below) but drops
   * the internal cap (`max-height: none; overflow: visible` via the `--grow` modifier
   * class) so there is NO internal scrollbar. An absolute descendant still expands the
   * scroll container's scrollable-overflow region (empirically verified in this
   * layout), so the NATURAL container scroll (the modal `.modal-body`, or the window
   * on the homepage) reaches the full list via its OUTER scrollbar. The optional
   * `onOpen` hook is called once the list is open so the consumer can MINIMALLY scroll
   * it into view (only when part is off-screen). Default OFF, so the OTHER shared
   * consumer (product config `config-groups.ts`) keeps its plain capped
   * `position:absolute; top:100%` overlay with its own internal scroll — this is what
   * makes the mechanic non-regressing.
   *
   * @see ADR-0010 2026-06-09 (overlay-grow supersedes D39's in-flow) and the `--grow`
   *      rule in `_combobox-visibility.scss`.
   */
  dropdownReveal?: DropdownReveal;
}

/**
 * The opt-in overlay-grow open-reveal hook (model-finder dropdown surfaces only,
 * ADR-0010 2026-06-09, supersedes D39). The two MF dropdown surfaces share ONE
 * mechanic; the per-surface scroll context (homepage window vs modal `.modal-body`) is
 * resolved by the consumer's `onOpen`, so the shared enhancer stays decoupled from the
 * MF view layer.
 */
export interface DropdownReveal {
  /**
   * Called after the list is opened as an uncapped overlay, with the (now full-height)
   * list element. The consumer minimally scrolls it into view (reusing the shared
   * `ScrollInView` machinery — out-of-view gate + reduced-motion + arbiter). Absent ⇒
   * overlay-grow only, no auto-scroll.
   */
  onOpen?: (list: HTMLElement) => void;
}

/**
 * Dropdown components for a single instance
 */
interface DropdownComponents {
  wrap: HTMLDivElement;
  input: HTMLInputElement;
  list: HTMLDivElement;
  select: HTMLSelectElement;
}

/**
 * Utility functions for dropdown enhancement
 */
export class DropdownUtils {
  /**
   * Normalize string for comparison (lowercase, remove accents)
   */
  public static normalize(s: string): string {
    return (s || '')
      .toString()
      .toLowerCase()
      .normalize('NFD')
      .replace(/[\u0300-\u036f]/g, '');
  }

  /**
   * Highlight first visible item in list
   */
  public static highlightFirstVisible(list: HTMLElement, hiddenClass: string): void {
    const firstVisible = list.querySelector(
      `.datte-dropdown-option:not(.${hiddenClass})`
    ) as HTMLElement;

    if (firstVisible) {
      // Clear previous active
      list
        .querySelectorAll('.datte-is-active')
        .forEach((n) => n.classList.remove('datte-is-active'));
      list
        .querySelectorAll('[aria-selected="true"]')
        .forEach((n) => n.setAttribute('aria-selected', 'false'));

      // Set new active
      firstVisible.classList.add('datte-is-active');
      firstVisible.setAttribute('aria-selected', 'true');
    }
  }
}

/* ============================================================================
 * Dropdown Enhancer
 * Converts native selects to searchable dropdowns
 * ========================================================================== */

export class DropdownEnhancer {
  public dropdowns: Map<number, DropdownComponents>;
  private _arrowToggleInProgress: boolean;
  private _originalSelections: Map<number, { value: string; name: string }>;
  private _docClick: (e: MouseEvent) => void;
  private config: DropdownConfig;
  private prefix: string;  // CSS class prefix

  constructor(config: DropdownConfig = {}) {
    this.dropdowns = new Map();
    this._arrowToggleInProgress = false;
    this._originalSelections = new Map();
    this._docClick = this.onDocumentClick.bind(this);

    this.config = {
      searchable: config.searchable !== false,
      buildClearOption: config.buildClearOption !== false,
      clearLabels: config.clearLabels || {},
      autoOpenOnFocus: config.autoOpenOnFocus !== false,
      filterParentId: config.filterParentId !== false,
      hidePrices: config.hidePrices !== false,
      hideZeroPrices: config.hideZeroPrices !== false,
      // dropdownReveal stays undefined unless the consumer opts in (default OFF).
      ...config,
    };

    // Set CSS class prefix (e.g., 'config', 'page')
    this.prefix = config.prefix || 'dropdown';
  }

  /**
   * Build CSS class name with variant prefix
   * Examples:
   *   prefix='dropdown' → 'datte-dropdown-wrapper'
   *   prefix='config' → 'datte-config-wrapper'
   *   prefix='mf-page' → 'datte-mf-page-wrapper' (for model-finder backward compatibility)
   */
  private cls(name: string): string {
    if (!this.prefix) {
      return `datte-dropdown-${name}`;
    }
    return `datte-${this.prefix}-${name}`;
  }

  /**
   * Enhance a native select element into a searchable dropdown
   */
  public enhance(select: HTMLSelectElement, level: number): void {
    const group = select.closest(
      '.datte-dropdown-group, .datte-mf-ssr-dropdown-group, .datte-mf-page-column, .cfg-group'
    );

    // Create dropdown wrapper
    const wrap = document.createElement('div');
    wrap.className = this.cls('combobox');
    wrap.setAttribute('role', 'combobox');
    wrap.setAttribute('aria-haspopup', 'listbox');
    wrap.setAttribute('aria-expanded', 'false');
    wrap.dataset.level = String(level);

    // Create search input
    const input = document.createElement('input');
    input.type = this.config.searchable ? 'search' : 'text';
    input.className = this.cls('combo-input');
    input.placeholder = this.getPlaceholder(group, level);
    input.autocomplete = 'off';
    input.disabled = select.disabled;
    if (!this.config.searchable) {
      input.readOnly = true;
    }

    // Create dropdown list
    const list = document.createElement('div');
    list.className = `${this.cls('combo-list')} ${this.cls('hidden')}`;
    list.setAttribute('role', 'listbox');

    // Hide original select with multiple methods for maximum compatibility
    const hiddenClass = this.cls('select-hidden');
    select.classList.add(hiddenClass);
    select.style.display = 'none';
    select.style.visibility = 'hidden';
    select.style.position = 'absolute';
    select.style.left = '-9999px';
    select.setAttribute('aria-hidden', 'true');
    select.setAttribute('tabindex', '-1');

    // Insert dropdown into DOM
    if (select.parentNode) {
      select.parentNode.insertBefore(wrap, select);
      wrap.appendChild(input);
      wrap.appendChild(list);
    } else {
      console.error('[DropdownEnhancer] Select element has no parent node, cannot enhance');
    }

    // Build initial list
    this.buildListForSelect(select, list, level);

    // Set initial value
    const selectedOpt = select.options[select.selectedIndex];
    if (selectedOpt && selectedOpt.value) {
      let initialValue = selectedOpt.dataset.name || selectedOpt.textContent || '';
      // Remove price from initial value if hidePrices is enabled
      if (this.config.hidePrices) {
        initialValue = initialValue.replace(/\s*\(\+\s*[\d,\.]+\s*€\)\s*/g, '').trim();
      } else if (this.config.hideZeroPrices) {
        // Check data attribute for proper zero price detection
        const hasPrice = selectedOpt.dataset.hasPrice === '1';
        if (!hasPrice) {
          // Remove price from display (pattern: "- PRICE *")
          initialValue = initialValue.replace(/\s*-\s*[^-]+€\s*\*?\s*$/g, '').trim();
        }
      }
      input.value = initialValue;
    }

    // Store components
    this.dropdowns.set(level, { wrap, input, list, select });

    // Bind events
    this.bindDropdownEvents(level);
  }

  /**
   * Get placeholder text for input
   */
  private getPlaceholder(group: Element | null, level: number): string {
    if (group) {
      const select = group.querySelector('select');
      const placeholderOption = select
        ? Array.from(select.options).find((opt) => !opt.value)
        : null;
      if (placeholderOption?.textContent?.trim()) {
        return placeholderOption.textContent.trim();
      }
    }
    return this.config.searchable ? 'Suchen …' : 'Auswählen …';
  }

  /**
   * Build dropdown list from select options
   */
  public buildListForSelect(
    select: HTMLSelectElement,
    list: HTMLDivElement,
    level: number
  ): void {
    list.innerHTML = '';
    const frag = document.createDocumentFragment();

    // Add "Clear selection" option if there's a selection
    if (select.value && this.config.buildClearOption) {
      const clearLabel = this.config.clearLabels?.[level] || 'Auswahl leeren';
      const clearItem = document.createElement('div');
      // IMPORTANT: Must include 'datte-dropdown-option' for click handler to work!
      clearItem.className = `datte-dropdown-option ${this.cls('combo-option')} ${this.cls('clear-option')}`;
      clearItem.setAttribute('role', 'option');
      clearItem.dataset.value = '';
      clearItem.dataset.isClear = 'true';
      clearItem.textContent = clearLabel; // styled apart via -clear-option; no dashes (wording rule)
      frag.appendChild(clearItem);
    }

    // Add all options from select
    Array.from(select.options).forEach((opt) => {
      if (!opt.value) return; // Skip placeholder

      const item = document.createElement('div');
      // Add both generic and prefix-specific classes for styling flexibility
      item.className = `datte-dropdown-option ${this.cls('combo-option')}`;
      item.setAttribute('role', 'option');

      // Copy data attributes
      item.dataset.value = opt.value;
      item.dataset.name = opt.dataset.name || opt.textContent?.trim() || '';

      if (opt.dataset.parentId != null) {
        item.dataset.parentId = opt.dataset.parentId;
      }

      if (opt.dataset.url) {
        item.dataset.url = opt.dataset.url;
      }

      if (opt.dataset.imageUrl) {
        item.dataset.imageUrl = opt.dataset.imageUrl;
      }

      // Copy hidden/disabled state
      const hiddenClass = this.cls('option-hidden');
      if (opt.classList.contains(hiddenClass) || opt.disabled) {
        item.classList.add(hiddenClass);
      }

      // Set text content, optionally hiding prices
      let displayText = opt.textContent?.trim() || '';
      if (this.config.hidePrices) {
        // Remove ALL price patterns like "(+ 0,00 €)", "(+ 5,00 €)", etc.
        displayText = displayText.replace(/\s*\(\+\s*[\d,\.]+\s*€\)\s*/g, '').trim();
      } else if (this.config.hideZeroPrices) {
        // Check data attribute for proper zero price detection
        const hasPrice = opt.dataset.hasPrice === '1';
        if (!hasPrice) {
          // Remove price from display (pattern: "- PRICE *")
          displayText = displayText.replace(/\s*-\s*[^-]+€\s*\*?\s*$/g, '').trim();
        }
      }

      // Wrap asterisk in red span for surcharge indicators
      if (displayText.includes('*')) {
        item.innerHTML = displayText.replace(/\*/g, '<span style="color: #d32f2f;">*</span>');
      } else {
        item.textContent = displayText;
      }
      frag.appendChild(item);
    });

    list.appendChild(frag);
  }

  /**
   * Rebuild list for a level (after dynamic content update)
   */
  public rebuild(level: number, select: HTMLSelectElement): void {
    const dropdown = this.dropdowns.get(level);
    if (!dropdown) return;
    this.buildListForSelect(select, dropdown.list, level);
  }

  /**
   * Update the clear label for a level (used for dynamic label switching, e.g. Serie → Serie 1)
   */
  public setClearLabel(level: number, label: string): void {
    if (!this.config.clearLabels) {
      this.config.clearLabels = {};
    }
    this.config.clearLabels[level] = label;
  }

  /**
   * Bind events for a dropdown
   */
  private bindDropdownEvents(level: number): void {
    const dropdown = this.dropdowns.get(level);
    if (!dropdown) return;

    const { wrap, input, list } = dropdown;

    // Input focus - open dropdown and select text
    input.addEventListener('focus', () => {
      if (this._arrowToggleInProgress) return;
      this.closeAllDropdownsExcept(level);
      if (this.config.autoOpenOnFocus) {
        this.openList(level);
      }
      if (this.config.searchable) {
        setTimeout(() => input.select(), 0);
      }
    });

    // Input click - open dropdown
    input.addEventListener('click', (e) => {
      if (this._arrowToggleInProgress) return;
      e.stopPropagation();
      this.closeAllDropdownsExcept(level);
      if (wrap.getAttribute('aria-expanded') !== 'true') {
        this.openList(level);
      }
    });

    // Input typing - filter options (only if searchable)
    if (this.config.searchable) {
      input.addEventListener('input', () => {
        const parentId = this.getParentValue(level);
        this.filterDropdown(level, parentId, input.value || '');
      });
    }

    // Keyboard navigation
    input.addEventListener('keydown', (e) => this.onDropdownKeydown(e, level));

    // Chevron toggle (click on wrapper outside input)
    wrap.addEventListener('mousedown', (e) => {
      if (e.target === input) return;
      if ((e.target as Element).closest('.datte-dropdown-option')) return;
      // Scrollbar clicks inside the list fire mousedown on the list element itself —
      // ignore them so the user can scroll without closing the dropdown.
      if ((e.target as Element).closest('[class*="-combo-list"]')) return;

      if (dropdown.select.disabled) return;

      this._arrowToggleInProgress = true;
      e.preventDefault();
      e.stopPropagation();

      const isOpen = wrap.getAttribute('aria-expanded') === 'true';
      this.closeAllDropdownsExcept(level);
      if (isOpen) {
        this.closeList(level);
      } else {
        if (this.config.searchable) {
          input.value = '';
        }
        this.openList(level);
      }

      setTimeout(() => {
        this._arrowToggleInProgress = false;
      }, 0);
    });

    // List option clicks
    list.addEventListener('click', (e) => {
      e.stopPropagation();
      const item = (e.target as Element).closest(
        '.datte-dropdown-option'
      ) as HTMLElement;
      const hiddenClass = this.cls('option-hidden');
      if (!item || item.classList.contains(hiddenClass)) return;
      this.pickDropdownOption(level, item);
    });

    // Document click handler (added once for all dropdowns)
    if (this.dropdowns.size === 1) {
      document.addEventListener('click', this._docClick, true);
    }
  }

  /**
   * Handle document click to close dropdowns
   */
  private onDocumentClick(e: MouseEvent): void {
    const target = e.target as Element;

    // Check if any dropdown is open
    let anyOpen = false;
    this.dropdowns.forEach(({ wrap }) => {
      if (wrap.getAttribute('aria-expanded') === 'true') {
        anyOpen = true;
      }
    });

    if (!anyOpen) return;

    // Check if click is inside any dropdown component (use generic selectors)
    // This checks for any element with class containing our component markers
    if (
      target.closest('[class*="-combobox"]') ||
      target.closest('[class*="-combo-input"]') ||
      target.closest('[class*="-combo-list"]') ||
      target.closest('[class*="-combo-option"]')
    ) {
      return;
    }

    // Close all dropdowns
    this.dropdowns.forEach((_, level) => this.closeList(level));
  }

  /**
   * Open dropdown list
   */
  public openList(level: number): void {
    const dropdown = this.dropdowns.get(level);
    if (!dropdown || dropdown.select.disabled) return;

    if (dropdown.wrap.getAttribute('aria-expanded') === 'true') return;

    // Store original selection
    const selectedOpt = dropdown.select.options[dropdown.select.selectedIndex];
    this._originalSelections.set(level, {
      value: dropdown.select.value,
      name:
        selectedOpt && selectedOpt.value
          ? selectedOpt.dataset.name || selectedOpt.textContent || ''
          : '',
    });

    // Unhide all options
    const hiddenClass = this.cls('option-hidden');
    Array.from(dropdown.select.options).forEach((opt) => {
      if (!opt.value) return;
      opt.classList.remove(hiddenClass);
    });

    // Rebuild list
    this.buildListForSelect(dropdown.select, dropdown.list, level);

    // Reset filter
    const parentId = this.getParentValue(level);
    this.filterDropdown(level, parentId, '');

    dropdown.wrap.setAttribute('aria-expanded', 'true');
    dropdown.list.classList.remove(this.cls('hidden'));

    if (this.config.dropdownReveal) {
      // OPT-IN (model-finder): the list stays an OVERLAY but drops its internal cap
      // (ADR-0010 2026-06-09, supersedes D39) — it floats over the fields below, has no
      // internal scrollbar, and the NATURAL container scroll (modal `.modal-body` /
      // window) reaches the full list via its OUTER scrollbar.
      this.applyGrowReveal(level, this.config.dropdownReveal);
    } else {
      // Default: position the absolute overlay list above or below based on available
      // space within the nearest clipping ancestor (e.g. .modal-content) / viewport.
      this.positionDropdownList(level);
    }

    DropdownUtils.highlightFirstVisible(dropdown.list, this.cls('option-hidden'));
  }

  /**
   * Choose whether the dropdown list opens downward (default) or upward.
   *
   * Only runs when the dropdown is inside a `.modal-content` element — that is
   * the only context where `overflow: hidden` on the ancestor clips the list and
   * makes the direction choice matter.  Outside a modal the list has a high
   * z-index and naturally extends past any ancestor, so the default CSS
   * (`top: 100%`) is correct and is left untouched.
   */
  private positionDropdownList(level: number): void {
    const dropdown = this.dropdowns.get(level);
    if (!dropdown) return;

    const { wrap, list } = dropdown;

    // Only apply smart positioning inside a modal-content clipping boundary.
    const clipper = wrap.closest<HTMLElement>('.modal-content');
    if (!clipper) return;

    const wrapRect = wrap.getBoundingClientRect();
    const clipRect = clipper.getBoundingClientRect();

    const spaceBelow = clipRect.bottom - wrapRect.bottom;
    const spaceAbove = wrapRect.top - clipRect.top;
    // CSS sets max-height: 300px; use that as the threshold to decide direction.
    const listMaxHeight = 300;

    if (spaceBelow < listMaxHeight && spaceAbove > spaceBelow) {
      // Not enough space below — open upward instead.
      const upHeight = Math.max(80, Math.min(listMaxHeight, spaceAbove - 8));
      list.style.top = 'auto';
      list.style.bottom = '100%';
      list.style.maxHeight = `${upHeight}px`;
      list.style.marginTop = '0';
      list.style.marginBottom = '-1px';
      list.classList.add('datte-dropdown-list--above');
    } else {
      // Default: open downward.
      const downHeight = Math.max(80, Math.min(listMaxHeight, spaceBelow - 8));
      list.style.top = '';
      list.style.bottom = '';
      list.style.maxHeight = `${downHeight}px`;
      list.style.marginTop = '';
      list.style.marginBottom = '';
      list.classList.remove('datte-dropdown-list--above');
    }
  }

  /**
   * Apply the opt-in OVERLAY-GROW open-reveal (ADR-0010 2026-06-09, supersedes D39):
   * keep the list a TRUE OVERLAY (the base `position: absolute; top: 100%` — it floats
   * over the fields/CTA below, NOT in-flow) but drop the internal cap (`max-height:
   * none; overflow: visible` via the `--grow` modifier class) so it has NO internal
   * scrollbar and renders the WHOLE option list. An absolute descendant still expands
   * the scroll container's scrollable-overflow region in this layout (empirically
   * verified), so a long list is reached by the NATURAL container scroll (the modal
   * `.modal-body` / the window) via its OUTER scrollbar. The optional `onOpen` hook
   * minimally scrolls the list into view (the consumer reuses the shared `ScrollInView`
   * machinery, resolving its own per-surface scroll context — so the shared enhancer
   * stays MF-agnostic).
   *
   * This SUPERSEDES D39's in-flow approach: in-flow pushed the fields down
   * (accordion), which the user asked to reverse. D39's premise that an absolute list
   * cannot grow its container was disproven here, so the overlay can stay absolute and
   * still uncap — best of both (floats over the fields AND the container scroll reaches
   * the full list).
   */
  private applyGrowReveal(level: number, reveal: DropdownReveal): void {
    const dropdown = this.dropdowns.get(level);
    if (!dropdown) return;
    const { list } = dropdown;

    // Clear any inline overrides a prior non-reveal open may have set (the base
    // absolute overlay position lives in CSS), then mark the list `--grow`; the CSS
    // `--grow` rule owns the cap removal (max-height:none; overflow:visible). The base
    // `position:absolute; top:100%` is KEPT, so the list floats over the fields below.
    list.style.top = '';
    list.style.bottom = '';
    list.style.marginTop = '';
    list.style.marginBottom = '';
    list.style.maxHeight = '';
    list.classList.remove('datte-dropdown-list--above');
    list.classList.add(this.cls('combo-list--grow'));

    reveal.onOpen?.(list);
  }

  /**
   * Close dropdown list
   */
  public closeList(level: number): void {
    const dropdown = this.dropdowns.get(level);
    if (!dropdown) return;

    if (dropdown.wrap.getAttribute('aria-expanded') === 'false') return;

    // Restore original selection if user didn't pick anything
    const original = this._originalSelections.get(level);
    if (original && original.value) {
      dropdown.select.value = original.value;
      dropdown.input.value = original.name;

      const opt = Array.from(dropdown.select.options).find(
        (o) => o.value === original.value
      );
      if (opt) opt.selected = true;
    }

    this._originalSelections.delete(level);

    dropdown.wrap.setAttribute('aria-expanded', 'false');
    dropdown.list.classList.add(this.cls('hidden'));
    this.clearListActive(dropdown.list);

    // Reset inline position overrides set by positionDropdownList + the overlay-grow
    // reveal modifier (ADR-0010 2026-06-09, supersedes D39). The list collapses back to
    // the closed state; closing does NOT restore any scroll position — the user stays
    // where they are (no jump-back), the explicit behaviour kept from D39.
    dropdown.list.style.top = '';
    dropdown.list.style.bottom = '';
    dropdown.list.style.maxHeight = '';
    dropdown.list.style.marginTop = '';
    dropdown.list.style.marginBottom = '';
    dropdown.list.classList.remove('datte-dropdown-list--above');
    dropdown.list.classList.remove(this.cls('combo-list--grow'));
  }

  /**
   * Close all dropdowns except the specified level
   */
  public closeAllDropdownsExcept(exceptLevel: number): void {
    this.dropdowns.forEach((_, level) => {
      if (level !== exceptLevel) {
        this.closeList(level);
      }
    });
  }

  /**
   * Filter dropdown options by parent and query
   */
  public filterDropdown(
    level: number,
    parentId: string | null,
    query: string
  ): boolean {
    const dropdown = this.dropdowns.get(level);
    if (!dropdown) return false;

    const { list, select } = dropdown;
    const q = DropdownUtils.normalize(query || '');
    const hiddenClass = this.cls('option-hidden');
    let anyVisible = false;

    // Check if options have parentId attributes
    const hasParentIds =
      this.config.filterParentId &&
      Array.from(select.options).some(
        (o) => o.value && o.dataset.parentId !== undefined
      );

    list.querySelectorAll('.datte-dropdown-option').forEach((item) => {
      const el = item as HTMLElement;

      // Always keep clear option visible
      if (el.dataset.isClear === 'true') {
        el.classList.remove(hiddenClass);
        return;
      }

      const value = el.dataset.value;
      const opt = Array.from(select.options).find((o) => o.value === value);
      if (!opt) return;

      // Parent filter
      const matchesParent =
        !hasParentIds ||
        parentId == null ||
        String(opt.dataset.parentId) === String(parentId);

      // Query filter
      const label = opt.dataset.name || opt.textContent || '';
      const matchesQuery = !q || DropdownUtils.normalize(label).includes(q);

      const visible = matchesParent && matchesQuery && !opt.disabled;
      if (visible) {
        el.classList.remove(hiddenClass);
        opt.classList.remove(hiddenClass);
        anyVisible = true;
      } else {
        el.classList.add(hiddenClass);
        opt.classList.add(hiddenClass);
        if (opt.selected) select.value = '';
      }
    });

    if (!dropdown.list.classList.contains(this.cls('hidden'))) {
      DropdownUtils.highlightFirstVisible(dropdown.list, hiddenClass);
    }

    return anyVisible;
  }

  /**
   * Handle keyboard navigation
   */
  private onDropdownKeydown(e: KeyboardEvent, level: number): void {
    const dropdown = this.dropdowns.get(level);
    if (!dropdown) return;

    const { list } = dropdown;
    const hiddenClass = this.cls('option-hidden');
    const items = Array.from(
      list.querySelectorAll(
        `.datte-dropdown-option:not(.${hiddenClass})`
      )
    ) as HTMLElement[];
    const current = list.querySelector('.datte-is-active') as HTMLElement;
    let idx = current ? items.indexOf(current) : -1;

    if (e.key === 'ArrowDown') {
      e.preventDefault();
      this.openList(level);
      idx = Math.min(idx + 1, items.length - 1);
      this.setActive(list, items[idx]);
    } else if (e.key === 'ArrowUp') {
      e.preventDefault();
      this.openList(level);
      idx = Math.max(idx - 1, 0);
      this.setActive(list, items[idx]);
    } else if (e.key === 'Enter') {
      if (!list.classList.contains(this.cls('hidden'))) {
        e.preventDefault();
        const pick =
          (list.querySelector('.datte-is-active') as HTMLElement) || items[0];
        if (pick) this.pickDropdownOption(level, pick);
      }
    } else if (e.key === 'Escape') {
      this.closeList(level);
    }
  }

  /**
   * Pick a dropdown option
   */
  private pickDropdownOption(level: number, item: HTMLElement): void {
    const dropdown = this.dropdowns.get(level);
    if (!dropdown) return;

    const value = item.dataset.value || '';
    const isClear = item.dataset.isClear === 'true';

    if (isClear) {
      // Clear selection
      dropdown.select.value = '';
      dropdown.input.value = '';

      // Explicitly select the placeholder option (first option with empty value)
      const placeholderOption = Array.from(dropdown.select.options).find(
        (opt) => !opt.value
      );
      if (placeholderOption) {
        placeholderOption.selected = true;
        dropdown.select.selectedIndex = placeholderOption.index;
      }

      this._originalSelections.delete(level);
      this.closeList(level);

      // Dispatch change event to trigger model-finder logic
      dropdown.select.dispatchEvent(new Event('change', { bubbles: true }));

      // Log for debugging
      console.log(`[DropdownEnhancer] Cleared level ${level}`);

      return;
    }

    // Normal selection
    let name = item.dataset.name || item.textContent || '';

    // Set select value first to find the option
    dropdown.select.value = value;
    const opt = Array.from(dropdown.select.options).find((o) => o.value === value);
    if (opt) opt.selected = true;

    // Remove price from displayed value if hidePrices is enabled
    if (this.config.hidePrices) {
      name = name.replace(/\s*\(\+\s*[\d,\.]+\s*€\)\s*/g, '').trim();
    } else if (this.config.hideZeroPrices) {
      // Check data attribute for proper zero price detection
      const hasPrice = opt?.dataset.hasPrice === '1';
      if (!hasPrice) {
        // Remove price from display (pattern: "- PRICE *")
        name = name.replace(/\s*-\s*[^-]+€\s*\*?\s*$/g, '').trim();
      }
    }

    dropdown.input.value = name;

    // Clear stored original selection
    this._originalSelections.delete(level);

    // Close dropdown
    this.closeList(level);

    // Trigger change event
    dropdown.select.dispatchEvent(new Event('change', { bubbles: true }));
  }

  /**
   * Set active state on list item
   */
  private setActive(list: HTMLElement, el: HTMLElement): void {
    if (!el) return;
    this.clearListActive(list);
    el.classList.add('datte-is-active');
    el.setAttribute('aria-selected', 'true');
    el.scrollIntoView({ block: 'nearest' });
  }

  /**
   * Clear active state from all items
   */
  private clearListActive(list: HTMLElement): void {
    list
      .querySelectorAll('.datte-is-active')
      .forEach((n) => n.classList.remove('datte-is-active'));
    list
      .querySelectorAll('[aria-selected="true"]')
      .forEach((n) => n.setAttribute('aria-selected', 'false'));
  }

  /**
   * Get parent value for cascading filter
   */
  private getParentValue(level: number): string | null {
    if (level <= 1) return null;

    const parentDropdown = this.dropdowns.get(level - 1);
    if (!parentDropdown) return null;

    return parentDropdown.select.value || null;
  }

  /**
   * Destroy dropdown (cleanup)
   */
  public destroy(): void {
    document.removeEventListener('click', this._docClick, true);
    this.dropdowns.forEach((dropdown) => {
      dropdown.wrap.remove();
      dropdown.select.classList.remove(this.cls('select-hidden'));
    });
    this.dropdowns.clear();
  }
}
